import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, ArrowLeft, User, CheckCircle, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import CinematicLayout from "../../components/CinematicLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForgotUsername = () => {
  const navigate = useNavigate();
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/forgot-username`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() })
      });

      if (response.ok) {
        toast.success("Account info sent! Check your email.");
        setSent(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send account info");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <CinematicLayout>
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card 
          className="w-full max-w-md"
          style={{
            background: 'linear-gradient(145deg, rgba(20, 15, 5, 0.95) 0%, rgba(15, 10, 5, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 215, 0, 0.2)',
            boxShadow: '0 0 50px rgba(255, 215, 0, 0.1)'
          }}
        >
          <CardHeader className="text-center">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'rgba(255, 215, 0, 0.15)' }}>
              <User size={32} className="text-[#FFD700]" />
            </div>
            <CardTitle className="text-2xl text-[#FFFFFF]" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              {sent ? "Email Sent!" : "Forgot Username?"}
            </CardTitle>
            <p className="text-[#E5C558] text-sm mt-2">
              {sent 
                ? "Check your email for your account information"
                : "Enter your email to receive your account details"
              }
            </p>
          </CardHeader>
          <CardContent>
            {!sent ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-[#E5C558]">Email Address</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#E5C558]" size={18} />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="pl-10 text-[#FFFFFF]"
                      style={{ background: 'rgba(26, 26, 26, 0.8)', borderColor: 'rgba(255, 215, 0, 0.2)' }}
                      required
                    />
                  </div>
                  <p className="text-xs text-[#71717A] mt-2">
                    We'll send your registered email and account name to this address
                  </p>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-[#FFD700] to-[#D4AF37] text-[#0a0500] font-semibold"
                  disabled={loading}
                >
                  {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : <Mail className="mr-2" size={18} />}
                  Send Account Info
                </Button>
              </form>
            ) : (
              <div className="text-center space-y-4">
                <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center" style={{ background: 'rgba(34, 197, 94, 0.2)' }}>
                  <CheckCircle size={40} className="text-[#22C55E]" />
                </div>
                <p className="text-[#E5C558]">
                  If an account exists with that email, we've sent your account information.
                </p>
                <Button 
                  onClick={() => navigate("/staff/login")}
                  className="w-full bg-gradient-to-r from-[#FFD700] to-[#D4AF37] text-[#0a0500] font-semibold"
                >
                  Go to Login
                </Button>
                <button
                  type="button"
                  onClick={() => setSent(false)}
                  className="w-full text-center text-sm text-[#E5C558] hover:text-[#FFD700]"
                >
                  Try a different email
                </button>
              </div>
            )}

            <div className="mt-6 pt-4 border-t border-[#27272A] space-y-3">
              <Link to="/staff/forgot-password" className="block text-center text-[#E5C558] hover:text-[#FFD700] text-sm">
                Forgot your password instead?
              </Link>
              <Link to="/staff/login" className="text-[#E5C558] hover:text-[#FFD700] text-sm flex items-center justify-center gap-2">
                <ArrowLeft size={16} />
                Back to Login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </CinematicLayout>
  );
};

export default ForgotUsername;
